import * as coreEntities from 'core/store/index';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import analyticsService from '../services/analyticsService';

/**
 * Custom hook for analytics functionality
 * Provides easy access to analytics methods with automatic user and context data
 */
function useAnalytics() {
  const user = useSelector((state) => coreEntities.auth.selectors.getUser(state));
  const tenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  const ingestionUrl = useSelector((state) => coreEntities.organization.selectors.getAnalyticsIngestionUrl(state));
  const authToken = useSelector((state) => coreEntities.auth.selectors.getAuthToken(state));
  const isInitialized = Boolean(ingestionUrl && authToken);

  /**
   * Get user data formatted for analytics SDK
   * @returns {Object|null} Formatted user data or null if no user
   */
  const getUserData = useCallback(() => {
    if (!user) return null;

    return {
      id: user.guid || user.id || user.user_id,
      role_guid: user.role_guid,
      school_state_guid: user.school_state_guid,
      school_city_guid: user.school_city_guid,
      school_guid: user.school_guid,
      school_name: user.school_name,
      is_tangerine_ai: user.is_tangerine_ai === 1,
      language: user.language || 'es',
    };
  }, [user]);

  /**
   * Generate or retrieve device ID
   * @returns {string} Device ID
   */
  const getDeviceId = useCallback(() => {
    const storedId = localStorage.getItem('device_id');
    if (storedId) return storedId;

    const newId = 'device_' + Math.random().toString(36).substring(2, 11);
    localStorage.setItem('device_id', newId);
    return newId;
  }, []);

  /**
   * Detect device type
   * @returns {string} Device type
   */
  const getDeviceType = useCallback(() => {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile ? 'mobile' : 'web';
  }, []);

  /**
   * Detect operating system
   * @returns {string} Operating system
   */
  const getOS = useCallback(() => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }, []);

  /**
   * Get platform (modern API with fallback)
   * @returns {string} Platform
   */
  const getPlatform = useCallback(() => {
    if (navigator.userAgentData?.platform) {
      return navigator.userAgentData.platform;
    }
    return getOS();
  }, [getOS]);

  /**
   * Get context data formatted for analytics SDK
   * @returns {Object} Formatted context data
   */
  const getContextData = useCallback(() => {
    return {
      device_id: getDeviceId(),
      device_type: getDeviceType(),
      os: getOS(),
      platform: getPlatform(),
      browser: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language || 'es',
      library: '@oneclick/analytics-sdk',
    };
  }, [getDeviceId, getDeviceType, getOS, getPlatform]);

  // `null` (falta URL, token, usuario o tenant) es la señal con la que el Tutor IA reintenta; un envío fallido resuelve `{ success: false }`.
  const trackEvent = useCallback(
    async (eventType, payload = {}) => {
      const tenantName = tenant?.name?.toLowerCase().replace(/ /g, '-');
      const userData = getUserData();
      if (!isInitialized || !userData || !tenantName) return null;

      return await analyticsService.trackEvent(eventType, {
        endpoint: ingestionUrl,
        apiKey: authToken,
        tenant: tenantName,
        user: userData,
        context: getContextData(),
        payload,
      });
    },
    [getUserData, getContextData, tenant, isInitialized, ingestionUrl, authToken]
  );

  return {
    trackEvent,
    isInitialized,
  };
}

export default useAnalytics;
